import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';

import TituloAdmin from '@components/TituloAdmin';
import BotonBuscar from '@components/BotonBuscar';
import Boton from '@components/Boton';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import Notify from '@components/Notify';
import { verCitasRangoApi } from "@api/wp/administracion/consultas/consultasApi";
import { getAllMedicosApi } from "@api/wp/medicos/medicosApi";
import { getAllEspecialidadesApi } from "@api/wp/especialidades/especialidadesApi";
import { getAllSedesApi } from "@api/wp/sedes/sedesApi";

import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import { tablaEstylos, tablaTheadEstylos, trStyle, thStyle } from '@styles/tablaEstylos';
import { colors } from '@styles/colors';
import Label from '@components/Label';
import { selectStyle } from '@styles/selecsEstylos';
import '@styles/modernTable.css';

function Consultas() {
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
  const [medicoSeleccionado, setMedicoSeleccionado] = useState('');
  const [medicos, setMedicos] = useState([]);
  const [especialidadSeleccionada, setEspecialidadSeleccionada] = useState('');
  const [especialidades, setEspecialidades] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState('');
  const [sedes, setSedes] = useState([]);
  const [verCanceladas, setVerCanceladas] = useState(false);
  const [notify, setNotify] = useState({ open: false, message: '', type: 'success' });

  // Refs para Select2
  const medicoSelectRef = useRef(null);
  const especialidadSelectRef = useRef(null);
  const sedeSelectRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/login');
    cargarDatos();
  }, [navigate]);

  const cargarDatos = async () => {
    try {
      const [resMedicos, resEspecialidades, resSedes] = await Promise.all([
        getAllMedicosApi(),
        getAllEspecialidadesApi(),
        getAllSedesApi()
      ]);
      if (resMedicos?.result) setMedicos(resMedicos.result);
      if (resEspecialidades?.result) setEspecialidades(resEspecialidades.result);
      if (resSedes?.result) setSedes(resSedes.result);
    } catch (error) {
      console.error('Error cargando filtros', error);
      setNotify({ open: true, message: 'Error al cargar filtros', type: 'error' });
    }
  };

  // Inicializar Select2 en Médicos
  useEffect(() => {
    try {
      const $ = window.$;
      if (!medicoSelectRef.current || !$ || !$.fn || !$.fn.select2) return;
      
      const $el = $(medicoSelectRef.current);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }

      $el.select2({
        placeholder: '-- Todos --',
        allowClear: true,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; }
        }
      });

      $el.on('change.select2react', function () {
        const value = $el.val() || '';
        setMedicoSeleccionado(value);
      });

      if (medicoSeleccionado) {
        $el.val(String(medicoSeleccionado)).trigger('change');
      }

      const motivosCancelacion = {
    "1": "Mi estado de salud cambió o ya no necesito la atención",
    "2": "Tengo un compromiso personal o laboral que coincide con la cita",
    "3": "No puedo desplazarme al lugar de atención",
    "4": "Decidí atenderme en otra institución o con otro profesional",
    "5": "No cuento con el dinero para el pago correspondiente"
  };

  return () => {
        if ($el.hasClass('select2-hidden-accessible')) {
          $el.off('change.select2react');
          $el.select2('destroy');
        }
      };
    } catch (e) {
      console.error('Error inicializando Select2 (médicos):', e);
    }
  }, [medicos]);

  // Inicializar Select2 en Especialidades
  useEffect(() => {
    try {
      const $ = window.$;
      if (!especialidadSelectRef.current || !$ || !$.fn || !$.fn.select2) return;
      
      const $el = $(especialidadSelectRef.current);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }

      $el.select2({
        placeholder: '-- Todas --',
        allowClear: true,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; }
        }
      });

      $el.on('change.select2react', function () {
        const value = $el.val() || '';
        setEspecialidadSeleccionada(value);
      });

      if (especialidadSeleccionada) {
        $el.val(String(especialidadSeleccionada)).trigger('change');
      }

      return () => {
        if ($el.hasClass('select2-hidden-accessible')) {
          $el.off('change.select2react');
          $el.select2('destroy');
        }
      };
    } catch (e) {
      console.error('Error inicializando Select2 (especialidades):', e);
    }
  }, [especialidades]);

  // Inicializar Select2 en Sedes
  useEffect(() => {
    try {
      const $ = window.$;
      if (!sedeSelectRef.current || !$ || !$.fn || !$.fn.select2) return;
      
      const $el = $(sedeSelectRef.current);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off('change.select2react');
        $el.select2('destroy');
      }

      $el.select2({
        placeholder: '-- Todas --',
        allowClear: true,
        width: '100%',
        theme: 'default',
        minimumResultsForSearch: 0,
        language: {
          noResults: function () { return 'No se encontraron resultados'; },
          searching: function () { return 'Buscando...'; }
        }
      });

      $el.on('change.select2react', function () {
        const value = $el.val() || '';
        setSedeSeleccionada(value);
      });

      if (sedeSeleccionada) {
        $el.val(String(sedeSeleccionada)).trigger('change');
      }

      return () => {
        if ($el.hasClass('select2-hidden-accessible')) {
          $el.off('change.select2react');
          $el.select2('destroy');
        }
      };
    } catch (e) {
      console.error('Error inicializando Select2 (sedes):', e);
    }
  }, [sedes]);

  const handleConsultarCheckbox = async (canceladas) => {
    if (!fechaInicio || !fechaFin) return;
    try {
      setCargando(true);
      const response = await verCitasRangoApi({ 
        fechaInicio, 
        fechaFin,
        oid_medico: medicoSeleccionado || null,
        oid_especialidad: especialidadSeleccionada || null,
        oid_sede: sedeSeleccionada || null,
        verCanceladas: canceladas
      });
      setCitas(response?.result || []);
      if (!response?.result?.length) {
        setNotify({ open: true, message: 'No se encontraron citas en ese rango', type: 'info' });
      }
    } catch (error) {
      console.error(error);
      setNotify({ open: true, message: 'Error al consultar citas', type: 'error' });
    } finally {
      setCargando(false);
    }
  };

  const handleConsultar = async () => {
    if (!fechaInicio || !fechaFin) {
      setNotify({ open: true, message: 'Seleccione un rango de fechas', type: 'warning' });
      return;
    }
    try {
      setCargando(true);
      const response = await verCitasRangoApi({ 
        fechaInicio, 
        fechaFin,
        oid_medico: medicoSeleccionado || null,
        oid_especialidad: especialidadSeleccionada || null,
        oid_sede: sedeSeleccionada || null,
        verCanceladas
      });
      setCitas(response?.result || []);
      if (!response?.result?.length) {
        setNotify({ open: true, message: 'No se encontraron citas en ese rango', type: 'info' });
      }
    } catch (error) {
      console.error(error);
      setNotify({ open: true, message: 'Error al consultar citas', type: 'error' });
    } finally {
      setCargando(false);
    }
  };

  const descargarExcel = () => {
    if (citas.length === 0) return;
    try {
      const headers = ['Cita ID', 'Fecha', 'Hora', 'Médico', 'Especialidad', 'Sede', 'Estado', 'Motivo Cancelación', 'Paciente ID'];
      const filas = citas.map(item => {
        const especialidadNombre = item.nombre_espe_db || item.nombre_espe || especialidades.find(e => e.id === item.oid_especialidad)?.nombre_especialidad || item.oid_especialidad || 'N/A';
        const sedeNombre = item.nombre_sede || sedes.find(s => s.id === item.oid_sede)?.nombre_sede || item.oid_sede || 'N/A';
        return [
          item.CitaID || 'N/A',
          item.fecha,
          item.hora,
          item.nombreMedico || 'N/A',
          especialidadNombre,
          sedeNombre,
          item.estado || 'N/A',
          item.nomMotivoCancel || '',
          item.id_paciente
        ];
      });

      const sep = ';';
      const esc = (val) => `"${String(val || '').replace(/"/g, '""')}"`;
      let contenido = '\uFEFF' + headers.map(esc).join(sep) + '\r\n';
      filas.forEach(f => contenido += f.map(esc).join(sep) + '\r\n');

      const blob = new Blob([contenido], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consultas_citas_${fechaInicio}_${fechaFin}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Error exportando', e);
    }
  };

  return (
    <div id="cajaPrincipal" style={cajaPrincipalStyle}>
      <LoadingOverlay visible={cargando} />
      <Notify open={notify.open} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, open: false })} />
      
      <TituloAdmin 
        titulo="Consultas de Citas" 
        imagen_titulo="extras"
        subtitulo="Consultar historial de citas por rango de fechas" 
      />

      <div style={cajaContenedorAdminStyle}>
        {/* Fila 1: Fechas y Botón de Consulta */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            columnGap: '7vh',
            rowGap: '2.5vh',
            marginBottom: '2vh',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Fecha Inicio:</Label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              style={{ ...selectStyle, marginTop: 'auto' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Fecha Fin:</Label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              style={{ ...selectStyle, marginTop: 'auto' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '1vh' }}>
            <Label aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
              &nbsp;
            </Label>
            <BotonBuscar
              onClick={handleConsultar}
              disabled={cargando}
              titulo="Consultar citas en el rango de fechas"
              varianteFiltros
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '2vh' }}>
            <Label aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
              &nbsp;
            </Label>
            <div style={{ display: 'flex', alignItems: 'center', columnGap: '1vh', marginTop: 'auto' }}>
              <input
                type="checkbox"
                id="verCanceladas"
                checked={verCanceladas}
                onChange={(e) => {
                  setVerCanceladas(e.target.checked);
                  setTimeout(() => handleConsultarCheckbox(e.target.checked), 0);
                }}
                style={{ width: '2vh', height: '2vh', cursor: 'pointer', flexShrink: 0 }}
              />
              <label htmlFor="verCanceladas" style={{ fontFamily: 'Inter', fontSize: '1.4vh', cursor: 'pointer' }}>
                Ver también canceladas
              </label>
            </div>
          </div>
        </div>

        {/* Fila 2: Filtros de Select2 */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            columnGap: '7vh',
            rowGap: '2.5vh',
            marginBottom: '3vh',
          }}
        >
          <div style={{ flex: 1, minWidth: '25vh', display: 'flex', flexDirection: 'column' }}>
            <Label>Médico (Opcional):</Label>
            <select
              ref={medicoSelectRef}
              value={medicoSeleccionado}
              onChange={(e) => setMedicoSeleccionado(e.target.value)}
              style={{ ...selectStyle, marginTop: 'auto' }}
            >
              <option value="">-- Todos --</option>
              {medicos.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '25vh', display: 'flex', flexDirection: 'column' }}>
            <Label>Especialidad (Opcional):</Label>
            <select
              ref={especialidadSelectRef}
              value={especialidadSeleccionada}
              onChange={(e) => setEspecialidadSeleccionada(e.target.value)}
              style={{ ...selectStyle, marginTop: 'auto' }}
            >
              <option value="">-- Todas --</option>
              {especialidades.map(e => (
                <option key={e.id} value={e.id}>
                  {e.nombre_especialidad} {e.nombre_empresa ? `- ${e.nombre_empresa}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '25vh', display: 'flex', flexDirection: 'column' }}>
            <Label>Sede (Opcional):</Label>
            <select
              ref={sedeSelectRef}
              value={sedeSeleccionada}
              onChange={(e) => setSedeSeleccionada(e.target.value)}
              style={{ ...selectStyle, marginTop: 'auto' }}
            >
              <option value="">-- Todas --</option>
              {sedes.map(s => <option key={s.id} value={s.id}>{s.nombre_sede}</option>)}
            </select>
          </div>
        </div>

        <div className="modern-table-container">
          {cargando ? (
            <LabelCarga nombre="consultas" />
          ) : (
            <table className="modern-table">
              <thead>
              <tr>
                <th>Cita ID</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Especialidad</th>
                <th>Sede</th>
                <th>Médico</th>
                <th>Estado</th>
                <th>Pago</th>
                {verCanceladas && <th>Motivo Cancelación</th>}
              </tr>
            </thead>
            <tbody>
              {citas.map((cita, i) => (
                <tr key={cita.id || i} style={{ backgroundColor: cita.estado === 'Cancelada' ? '#ffe6e6' : undefined }}>
                  <td>{cita.CitaID || 'N/A'}</td>
                  <td>{cita.fecha}</td>
                  <td>{cita.hora}</td>
                  <td>{cita.nombre_espe_db || cita.nombre_espe || especialidades.find(e => e.id === cita.oid_especialidad)?.nombre_especialidad || cita.oid_especialidad || 'N/A'}</td>
                  <td>{cita.nombre_sede || sedes.find(s => s.id === cita.oid_sede)?.nombre_sede || cita.oid_sede || 'N/A'}</td>
                  <td>{cita.nombreMedico || 'N/A'}</td>
                  <td>{cita.estado}</td>
                  <td style={{ textAlign: 'center' }}>
                    {Number(cita?.idResBold ?? 0) > 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh' }} title="Pago en linea">
                        <CheckCircleIcon style={{ color: colors.success, fontSize: '3.0vh' }} />
                        <CreditCardIcon style={{ color: colors.success, fontSize: '3.0vh' }} />
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh' }} title="Pago en ventanilla">
                        <PersonIcon style={{ color: colors.gray600, fontSize: '3.0vh' }} />
                        <StorefrontIcon style={{ color: colors.gray600, fontSize: '3.0vh' }} />
                      </span>
                    )}
                  </td>
                  {verCanceladas && (
                    <td>
                      {cita.estado === 'Cancelada' ? cita.nomMotivoCancel || '' : ''}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        <div style={{ textAlign: 'right', marginTop: '2vh' }}>
            <Boton 
              label="Descargar Excel" 
              onClick={descargarExcel} 
              disabled={!citas.length} 
              color={colors.primary} 
              ancho="25vh" 
              alto="5vh" 
            />
        </div>
      </div>
    </div>
  );
}

export default Consultas;
